import { useEffect } from "react";
import { useOnboardingLoading } from "../../hooks/useOnboardingLoading";
import { useOnboardingLogic } from "../../hooks/useOnboardingLogic";
import { useQuestionState } from "../../hooks/useQuestionState";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import { OnboardingAnalyticsService } from "../../services/onboardingAnalyticsService";
import * as QuestionService from "../../services/questionService";
import type { OnboardingWizardProps, Question } from "../../types/onboarding";
import { OnboardingFooter } from "./OnboardingFooter";
import { OnboardingHeader } from "./OnboardingHeader";
import { OnboardingHeadingCard } from "./OnboardingHeadingCard";
import styles from "./OnboardingWizard.module.css";
import { ProgressBar } from "./ProgressBar";
import { QuestionRenderer } from "./QuestionRenderer";

export function OnboardingWizard({
	onComplete,
	userData,
}: OnboardingWizardProps) {
	const {
		currentScreenIndex,
		setCurrentScreenIndex,
		answers,
		screens,
		handleInputChange,
		saveCurrentAnswer,
	} = useOnboardingLogic(userData);

	const { t } = useClientTranslations("onboarding");
	const { logEvent } = useAnalytics();
	const { isLoading, executeWithLoading } = useOnboardingLoading();

	const analyticsService = new OnboardingAnalyticsService({ logEvent });

	const {
		showValidation,
		handleShowValidation,
		currentQuestion,
		isAnswered,
		isLast,
		shouldTriggerValidation,
	} = useQuestionState(screens, currentScreenIndex, answers);

	// Extract title translation to service
	const getTranslatedTitle = (question: Question) =>
		QuestionService.getTranslatedQuestionTitle(question, t);

	useEffect(() => {
		if (screens.length === 0 || !currentQuestion) return;

		analyticsService.trackScreenViewed(
			currentScreenIndex,
			currentQuestion,
			screens.length,
		);
	}, [currentScreenIndex, screens, currentQuestion, analyticsService]);

	const handleNext = async () => {
		if (!currentQuestion) return;

		analyticsService.trackNextClicked(
			currentQuestion,
			currentScreenIndex,
			isLast,
			isAnswered,
			screens.length,
		);

		// Show validation for incomplete forms that require validation
		if (shouldTriggerValidation() && !isAnswered) {
			handleShowValidation();
			return;
		}

		try {
			await executeWithLoading(async () => {
				await saveCurrentAnswer();

				if (!isLast) {
					setCurrentScreenIndex(currentScreenIndex + 1);
				} else {
					await handleComplete();
				}
			});
		} catch (error) {
			console.error("Error in handleNext:", error);
		}
	};

	const handleComplete = async () => {
		analyticsService.trackCompleteClicked(answers, screens.length);

		try {
			await onComplete(answers);
			analyticsService.trackCompletedSuccessfully(answers, screens.length);
		} catch (error) {
			console.error("Error completing onboarding:", error);
			analyticsService.trackCompletionError(error, answers);
		}
	};

	const handleBack = () => {
		if (currentScreenIndex === 0) return;

		analyticsService.trackBackClicked(currentScreenIndex, screens.length);
		setCurrentScreenIndex(currentScreenIndex - 1);
	};

	if (screens.length === 0) {
		return (
			<div className={styles.container}>
				<div className={styles.completedCard}>
					<h2 className={styles.completedTitle}>Welcome back!</h2>
					<p className={styles.completedText}>
						Your profile is already complete.
					</p>
					<button
						type="button"
						onClick={() => {
							analyticsService.trackContinueToEra("already_complete");
							handleComplete();
						}}
						className={styles.completedButton}
					>
						Continue to Era
					</button>
				</div>
			</div>
		);
	}

	if (!currentQuestion) {
		return (
			<div className={styles.container}>
				<div className={styles.completedCard}>
					<h2 className={styles.completedTitle}>Welcome back!</h2>
					<p className={styles.completedText}>
						Your profile is already complete.
					</p>
					<button
						type="button"
						onClick={() => {
							analyticsService.trackContinueToEra("already_complete");
							handleComplete();
						}}
						className={styles.completedButton}
					>
						Continue to Era
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<div className={styles.headerWrapper}>
				<OnboardingHeader
					onBack={handleBack}
					showBackButton={currentScreenIndex > 0}
				/>
			</div>

			<div className={styles.progressBarWrapper}>
				<ProgressBar
					currentIndex={currentScreenIndex}
					total={screens.length}
					questionType={currentQuestion.type}
				/>
			</div>

			<div className={styles.content}>
				<div className={styles.contentTop}>
					{currentQuestion.type === "setup-profile" ||
					currentQuestion.type === "setup-profile-details" ? (
						<div className={styles.titleSection}>
							<h1 className={styles.mainTitle}>{t("setupProfile.title")}</h1>
							<p className={styles.subtitle}>{t("setupProfile.subtitle")}</p>
						</div>
					) : currentQuestion.type === "fertility-status" ||
						currentQuestion.type === "fertility-role" ? (
						<h1 className={styles.simpleTitleBlack}>
							{getTranslatedTitle(currentQuestion)}
						</h1>
					) : currentQuestion.type === "fertility-journey-types" ? (
						<div className={styles.titleSection}>
							<h1 className={styles.simpleTitleBlack}>
								{t("fertilityJourneyTypes.title")}
							</h1>
							<p className={styles.subtitle}>
								{t("fertilityJourneyTypes.subtitle")}
							</p>
						</div>
					) : currentQuestion.type === "needs-most" ? (
						<div className={styles.titleSection}>
							<h1 className={styles.simpleTitleBlack}>
								{t("needsMost.title")}
							</h1>
							<p className={styles.subtitle}>{t("needsMost.subtitle")}</p>
						</div>
					) : currentQuestion.type === "how-did-you-hear" ? (
						<div className={styles.titleSection}>
							<h1 className={styles.simpleTitleBlack}>
								{t("howDidYouHear.title")}
							</h1>
							<p className={styles.subtitle}>{t("howDidYouHear.subtitle")}</p>
						</div>
					) : currentQuestion.type === "additional-info" ? (
						<div className={styles.titleSection}>
							<h1 className={styles.simpleTitleBlack}>
								{t("additionalInfo.title")}
							</h1>
							<p className={styles.subtitle}>{t("additionalInfo.subtitle")}</p>
						</div>
					) : (
						<OnboardingHeadingCard text={getTranslatedTitle(currentQuestion)} />
					)}

					<div className={styles.questionContainer}>
						<QuestionRenderer
							question={currentQuestion}
							answers={answers}
							onInputChange={handleInputChange}
							showValidation={showValidation}
						/>
					</div>
				</div>

				<OnboardingFooter
					isAnswered={isAnswered}
					isLoading={isLoading}
					onNext={handleNext}
					isLastScreen={isLast}
					questionType={currentQuestion.type}
					answers={answers}
					onInputChange={handleInputChange}
					onShowValidation={handleShowValidation}
					showValidation={showValidation}
				/>
			</div>
		</div>
	);
}
